import {CLMimeType} from 'common/src/modules/media/media.types';
import isPSD from 'common/src/utils/mediaUtils/isPSD';
import getMimeType from 'common/src/utils/mediaUtils/getMimeType';
import {
  imageMediaMimeTypes,
  videoMediaMimeTypes,
  fontMediaMimeTypes,
  subtitlesMediaMimeTypes,
} from 'common/src/modules/media/media.consts';
import getExtension from 'common/src/modules/media/utils/getExtension';
import {FolderAssetUploadItem} from '../assetDisplayThunkUtils';

enum FileTypeCategory {
  Image = 'image',
  Video = 'video',
  Audio = 'audio',
  Font = 'font',
  Excel = 'excel',
  Psd = 'psd',
  Subtitle = 'subtitle',
  Web = 'web',
  Other = 'other',
}

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;

// Max file sizes in bytes
const MaxImageBytesSize = 10 * BYTES_PER_MB; // 10 MB
const MaxAudioBytesSize = 10 * BYTES_PER_MB; // 10 MB
const MaxVideoBytesSize = 300 * BYTES_PER_MB; // 300 MB
const MaxWebFilesSize = 500 * BYTES_PER_KB; // 500 KB
const MaxFontBytesSize = 2 * BYTES_PER_MB; // 2 MB
const MaxExcelBytesSize = 100 * BYTES_PER_MB; // 100 MB
const MaxPsdBytesSize = 100 * BYTES_PER_MB; // 100 MB
const MaxSubtitleBytesSize = 2 * BYTES_PER_MB; // 2 MB
const DefaultMaxBytesSize = 100 * BYTES_PER_MB; // 100 MB

// File type category to max size mapping
const maxSizeByCategory: Record<FileTypeCategory, number> = {
  [FileTypeCategory.Image]: MaxImageBytesSize,
  [FileTypeCategory.Video]: MaxVideoBytesSize,
  [FileTypeCategory.Audio]: MaxAudioBytesSize,
  [FileTypeCategory.Font]: MaxFontBytesSize,
  [FileTypeCategory.Excel]: MaxExcelBytesSize,
  [FileTypeCategory.Psd]: MaxPsdBytesSize,
  [FileTypeCategory.Subtitle]: MaxSubtitleBytesSize,
  [FileTypeCategory.Web]: MaxWebFilesSize,
  [FileTypeCategory.Other]: DefaultMaxBytesSize,
};

// Audio mime types
const audioMimeTypes = [
  CLMimeType.MP3,
  CLMimeType.WAV,
  CLMimeType.M4A,
  CLMimeType.OGG,
  CLMimeType.FLACC,
];

// Excel mime types
const excelMimeTypes = [CLMimeType.XLS, CLMimeType.XLSX];
const excelExtensions = excelMimeTypes
  .map((mimeType) => getExtension(mimeType))
  .filter(Boolean) as string[];

// Web file extensions (HTML, CSS, JS) - not all web mime types are in CLMimeType enum
const webFileExtensions = ['html', 'css', 'js'];

// Web file mime types
const webFileMimeTypes = ['text/html', 'text/css', 'text/javascript', 'application/javascript'];

// Subtitle extensions - derive from mime types
const subtitleExtensions = subtitlesMediaMimeTypes
  .map((mimeType) => getExtension(mimeType))
  .filter(Boolean) as string[];

/**
 * Determines the file type category for size validation
 * Checks mime type first, then falls back to extension if mime type is missing or doesn't match
 */
function getFileTypeCategory(file: File): FileTypeCategory {
  const mimeType = file.type.toLowerCase();
  const extension = getMimeType(file).subType?.toLowerCase();

  // Check PSD first (special case)
  if (isPSD(file) || mimeType === CLMimeType.PSD.toLowerCase()) {
    return FileTypeCategory.Psd;
  }

  // Check Excel files - mime type first, then extension fallback
  if (mimeType && excelMimeTypes.some((excelType) => mimeType === excelType.toLowerCase())) {
    return FileTypeCategory.Excel;
  }

  if (excelExtensions.includes(extension || '')) {
    return FileTypeCategory.Excel;
  }

  // Check web files - mime type first, then extension fallback
  if (mimeType && webFileMimeTypes.includes(mimeType)) {
    return FileTypeCategory.Web;
  }

  if (webFileExtensions.includes(extension || '')) {
    return FileTypeCategory.Web;
  }

  // Check images - mime type check (has fallback to startsWith)
  if (
    mimeType &&
    (imageMediaMimeTypes.some((imageType) => mimeType === imageType.toLowerCase()) ||
      mimeType.startsWith('image/'))
  ) {
    return FileTypeCategory.Image;
  }

  // Check videos - mime type check (has fallback to startsWith)
  if (
    mimeType &&
    (videoMediaMimeTypes.some((videoType) => mimeType === videoType.toLowerCase()) ||
      mimeType.startsWith('video/'))
  ) {
    return FileTypeCategory.Video;
  }

  // Check audio - mime type first, then extension fallback
  if (
    mimeType &&
    (audioMimeTypes.some((audioType) => mimeType === audioType.toLowerCase()) ||
      mimeType.startsWith('audio/'))
  ) {
    return FileTypeCategory.Audio;
  }

  // Check fonts - mime type check (has fallback to startsWith)
  if (
    mimeType &&
    (fontMediaMimeTypes.some((fontType) => mimeType === fontType.toLowerCase()) ||
      mimeType.startsWith('font/'))
  ) {
    return FileTypeCategory.Font;
  }

  // Check subtitles - mime type first, then extension fallback
  if (
    mimeType &&
    subtitlesMediaMimeTypes.some((subtitleType) => mimeType === subtitleType.toLowerCase())
  ) {
    return FileTypeCategory.Subtitle;
  }

  if (subtitleExtensions.includes(extension || '')) {
    return FileTypeCategory.Subtitle;
  }

  return FileTypeCategory.Other;
}

/**
 * Gets the maximum allowed size in bytes for a file type category
 */
function getMaxSizeForCategory(category: FileTypeCategory): number {
  return maxSizeByCategory[category] ?? DefaultMaxBytesSize;
}

/**
 * Validates file sizes against their type-specific maximums
 * @param items - Array of files with their paths to validate
 * @returns Error message with file name if validation fails, null if all files are valid
 */
export default function validateFileSizes(items: FolderAssetUploadItem[]): string | null {
  for (const {file} of items) {
    const category = getFileTypeCategory(file);
    const maxSize = getMaxSizeForCategory(category);

    if (file.size > maxSize) {
      return `File "${file.name}" exceeds the maximum size limit for ${category} files`;
    }
  }

  return null;
}
